import React from 'react';
import {
  Card,
  Heading,
  TextStyle,
  SkeletonBodyText
} from '@superops/ui-design-system';
import { AccountClient } from '@nile/graphql';
import { useQuery } from '@apollo/client';
import { SVGIcon } from '@superops/super-icons';
import { GET_AI_FEATURE_METRIC_DATA } from '@nile/banyan/src/Graphql/Query/ai';
import AIFeaturesHook from '@nile/components/src/Utils/aiFeaturesHook';
import { FormattedMessage } from 'react-intl';

const PATCH_AGENT_CONSTANT = 'PATCH_AGENT';
type Props = {};
const stabilitySeverityMapping = {
  totalPatches: { icon: '', label: 'patch.analysed' },
  stablePatches: { icon: 'AIStable', label: 'safe.patches.fount' },
  neutralPatches: { icon: 'AIWarning', label: 'neutral.patches.found' },
  cautionPatches: { icon: 'AICaution', label: 'unsafe.patches.found' }
};

const InfoCard = ({ value, icon, label }) => {
  return (
    <div className="min-w200">
      <div className="fx g6">
        {icon ? <SVGIcon name={icon} /> : null}
        <div className="fx col g10">
          <TextStyle>
            <FormattedMessage id={label} />
          </TextStyle>
          <Heading element="h2" weight="SemiBold">
            {value || 0}
          </Heading>
        </div>
      </div>
    </div>
  );
};

const PatchInsightsCard = (props: Props) => {
  const aiFeatureHook = AIFeaturesHook();
  const { getIsAIFeatureEnabled, getAIFeatureData } = aiFeatureHook;
  const aiFeatureData = getAIFeatureData(PATCH_AGENT_CONSTANT);
  const isEnabled = getIsAIFeatureEnabled(PATCH_AGENT_CONSTANT);
  const displayIconUrl = aiFeatureData?.displayIconUrl;

  const { data, loading, error } = useQuery(GET_AI_FEATURE_METRIC_DATA, {
    client: AccountClient,
    fetchPolicy: 'no-cache',
    skip: !isEnabled,
    variables: {
      input: {
        featureId: aiFeatureData?.featureId,
        constant: PATCH_AGENT_CONSTANT
      }
    }
  });
  if (!isEnabled) {
    return null;
  }

  const dataKeys = [
    'totalPatches',
    'cautionPatches',
    'stablePatches',
    'neutralPatches'
  ];
  if (loading) {
    return (
      <div className="mt-20">
        <div className="flex g40 w100 h180 card-style br-none">
          <div className="w100 ">
            <SkeletonBodyText lines={3} />
          </div>
          <div className="w100">
            <SkeletonBodyText lines={3} />
          </div>
          <div className="w100">
            <SkeletonBodyText lines={3} />
          </div>
          <div className="w100">
            <SkeletonBodyText lines={3} />
          </div>
        </div>
      </div>
    );
  }
  if (error) {
    console.error(error);
    return null;
  }
  return (
    <div className="mt-20">
      <Card padding="sizeSmall">
        <div className="flex g10 actr">
          <div className="flex">
            <img
              src={displayIconUrl}
              alt="patch agent"
              loading="lazy"
              width={20}
              height={20}
              className="rounded-3px w-20 h-20"
            />
          </div>
          <div>
            <Heading element="h3" weight="SemiBold">
              <FormattedMessage id="patch.agent" />
            </Heading>
          </div>
        </div>
        <div className="mt20 w100">
          <div className="flex g40 wrap">
            {dataKeys.map((key) => (
              <InfoCard
                key={key}
                heading={key}
                value={data?.getAIFeatureMetricData?.data?.[key]}
                {...stabilitySeverityMapping[key]}
              />
            ))}
          </div>
        </div>
      </Card>
    </div>
  );
};

export default PatchInsightsCard;
